import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isValidSubdomain, isReservedSubdomain } from '../utils/tenant'
import { api } from '../services/api'
import './Register.css'

interface RegistrationData {
  clinicName: string
  contactEmail: string
  subdomain: string
  productType: 'ppt' | 'pms' | ''
  contactPersonName: string
  phoneNumber?: string
  /**
   * Optional username for the initial user account.
   * Admin role will be determined by role assignment (feature to be developed).
   */
  username?: string
  termsAccepted: boolean
  privacyAccepted: boolean
  pcrsResponsibilityAccepted: boolean
}

const TERMS_VERSION = '1.0'
const PRIVACY_VERSION = '1.0'
const PCRS_RESPONSIBILITY_VERSION = '1.0'

export function Register() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<RegistrationData>({
    clinicName: '',
    contactEmail: '',
    subdomain: '',
    productType: '',
    contactPersonName: '',
    phoneNumber: '',
    username: '',
    termsAccepted: false,
    privacyAccepted: false,
    pcrsResponsibilityAccepted: false,
  })
  const [subdomainAvailability, setSubdomainAvailability] = useState<{
    checked: boolean
    available: boolean | null
    checking: boolean
  }>({
    checked: false,
    available: null,
    checking: false,
  })
  const [errors, setErrors] = useState<Partial<Record<keyof RegistrationData, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [requiresVerification, setRequiresVerification] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  // Check subdomain availability when it changes
  useEffect(() => {
    const subdomain = formData.subdomain.toLowerCase().trim()
    
    if (!subdomain) {
      setSubdomainAvailability({ checked: false, available: null, checking: false })
      return
    }

    // Validate format first
    if (!isValidSubdomain(subdomain)) {
      setSubdomainAvailability({ checked: true, available: false, checking: false })
      return
    }

    // Check if reserved
    if (isReservedSubdomain(subdomain)) {
      setSubdomainAvailability({ checked: true, available: false, checking: false })
      return
    }

    // Debounce API call
    const timeoutId = setTimeout(async () => {
      setSubdomainAvailability(prev => ({ ...prev, checking: true }))
      try {
        const response = await api.get<{ available: boolean; subdomain: string; message: string }>(
          '/check-subdomain',
          { params: { subdomain } }
        )
        setSubdomainAvailability({
          checked: true,
          available: response.data.available,
          checking: false,
        })
      } catch (error: any) {
        console.error('Error checking subdomain availability:', error)
        setSubdomainAvailability({
          checked: true,
          available: null,
          checking: false,
        })
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [formData.subdomain])

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationData, string>> = {}

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = 'Clinic name is required'
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address'
    }

    if (!formData.productType) {
      newErrors.productType = 'Please select a product'
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required'
    } else if (!isValidSubdomain(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain must be 3-63 characters, lowercase alphanumeric with hyphens only'
    } else if (isReservedSubdomain(formData.subdomain)) {
      newErrors.subdomain = 'This subdomain is reserved and cannot be used'
    } else if (subdomainAvailability.checked && !subdomainAvailability.available) {
      newErrors.subdomain = 'This subdomain is already taken'
    }

    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required'
    }

    // Username is optional, but if provided, must be valid
    if (formData.username && formData.username.trim().length > 0 && formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters if provided'
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Terms and Conditions'
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = 'You must accept the Privacy Notice'
    }

    if (!formData.pcrsResponsibilityAccepted) {
      newErrors.pcrsResponsibilityAccepted = 'You must confirm PCRS responsibility and indemnity'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!validateForm()) {
      return
    }

    if (!subdomainAvailability.checked || !subdomainAvailability.available) {
      setSubmitError('Please wait for subdomain availability check to complete')
      return
    }

    setIsSubmitting(true)

    try {
      const acceptanceTimestamp = new Date().toISOString()
      const response = await api.post<{
        message: string
        email?: string
        requiresVerification?: boolean
        tenantId?: string
        subdomain?: string
        status?: string
        estimatedTime?: string
      }>('/register', {
        clinicName: formData.clinicName.trim(),
        contactEmail: formData.contactEmail.toLowerCase().trim(),
        subdomain: formData.subdomain.toLowerCase().trim(),
        contactPersonName: formData.contactPersonName.trim(),
        phoneNumber: formData.phoneNumber?.trim(),
        username: formData.username?.trim(),
        productType: formData.productType || 'pms',
        termsAccepted: formData.termsAccepted,
        termsVersion: TERMS_VERSION,
        termsAcceptedAt: formData.termsAccepted ? acceptanceTimestamp : undefined,
        privacyAccepted: formData.privacyAccepted,
        privacyVersion: PRIVACY_VERSION,
        privacyAcceptedAt: formData.privacyAccepted ? acceptanceTimestamp : undefined,
        pcrsResponsibilityAccepted: formData.pcrsResponsibilityAccepted,
        pcrsResponsibilityVersion: PCRS_RESPONSIBILITY_VERSION,
        pcrsResponsibilityAcceptedAt: formData.pcrsResponsibilityAccepted ? acceptanceTimestamp : undefined,
      })

      if (response.data.requiresVerification) {
        setRequiresVerification(true)
        setSubmitSuccess(true)
      } else {
        // Legacy flow (should not happen with new implementation)
        setSubmitSuccess(true)
      }
    } catch (error: any) {
      console.error('Registration error:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to register. Please try again.'
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof RegistrationData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value as RegistrationData[keyof RegistrationData] }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
    // Clear subdomain availability when subdomain changes
    if (field === 'subdomain') {
      setSubdomainAvailability({ checked: false, available: null, checking: false })
    }
  }

  const handleCheckboxChange = (field: keyof Pick<RegistrationData, 'termsAccepted' | 'privacyAccepted' | 'pcrsResponsibilityAccepted'>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.checked }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const getSubdomainStatus = () => {
    if (!formData.subdomain.trim()) {
      return null
    }

    if (subdomainAvailability.checking) {
      return { text: 'Checking availability...', className: 'checking' }
    }

    if (!isValidSubdomain(formData.subdomain)) {
      return { text: 'Invalid format', className: 'invalid' }
    }

    if (isReservedSubdomain(formData.subdomain)) {
      return { text: 'Reserved subdomain', className: 'invalid' }
    }

    if (subdomainAvailability.checked) {
      if (subdomainAvailability.available) {
        return { text: '✓ Available', className: 'available' }
      } else {
        return { text: '✗ Not available', className: 'unavailable' }
      }
    }

    return null
  }

  const subdomainStatus = getSubdomainStatus()

  const handleResendVerification = async () => {
    setIsSubmitting(true)
    setSubmitError(null)
    setResendSuccess(false)

    try {
      await api.post('/resend-verification', {
        email: formData.contactEmail.toLowerCase().trim(),
        subdomain: formData.subdomain.toLowerCase().trim(),
      })
      setSubmitError(null)
      setResendSuccess(true)
    } catch (error: any) {
      console.error('Resend verification error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Failed to resend verification email.'
      setSubmitError(errorMessage)
      setResendSuccess(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess && requiresVerification) {
    return (
      <div className="register-container">
        <div className="register-success">
          <div className="success-icon">✓</div>
          <h1>Registration Received!</h1>
          <p>
            We've sent a verification email to <strong>{formData.contactEmail}</strong>.
          </p>
          <p>
            Please check your email and click the verification link to complete your registration
            and begin provisioning your clinic instance at{' '}
            <strong>{formData.subdomain.toLowerCase()}.clnicl.ie</strong>.
          </p>
          <p className="verification-note">
            The verification link will expire in 24 hours. If you don't see the email, please check
            your spam folder.
          </p>
          <div className="verification-actions">
            <button
              className="btn-secondary"
              onClick={handleResendVerification}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <button
              className="btn-primary"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
          {resendSuccess && (
            <div className="success-banner" style={{ marginTop: '20px', background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '1rem', borderRadius: '6px' }}>
              ✓ Verification email has been resent. Please check your inbox.
            </div>
          )}
          {submitError && (
            <div className="error-banner" style={{ marginTop: '20px' }}>
              {submitError}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (submitSuccess && !requiresVerification) {
    // Legacy success flow (should not happen with new implementation)
    return (
      <div className="register-container">
        <div className="register-success">
          <div className="success-icon">✓</div>
          <h1>Registration Successful!</h1>
          <p>
            Your clinic registration has been received. We're provisioning your dedicated
            instance now.
          </p>
          <p>
            You will receive an email at <strong>{formData.contactEmail}</strong> when
            your instance is ready (typically 5-10 minutes).
          </p>
          <p>
            Once ready, you can access your clinic at:{' '}
            <strong>{formData.subdomain.toLowerCase()}.clnicl.ie</strong>
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate('/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="register-container">
      <div className="register-form-wrapper">
        <div className="register-header">
          <h1>Register Your Clinic</h1>
          <p>Get your own dedicated Clnicl instance in minutes</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="clinicName">
              Clinic Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicName"
              value={formData.clinicName}
              onChange={handleChange('clinicName')}
              placeholder="e.g., Trinity Family Wellness Centre"
              className={errors.clinicName ? 'error' : ''}
            />
            {errors.clinicName && (
              <span className="error-message">{errors.clinicName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="contactEmail">
              Contact Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange('contactEmail')}
              placeholder="admin@yourclinic.ie"
              className={errors.contactEmail ? 'error' : ''}
            />
            {errors.contactEmail && (
              <span className="error-message">{errors.contactEmail}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subdomain">
              Preferred Subdomain <span className="required">*</span>
            </label>
            <div className="subdomain-input-wrapper">
              <input
                type="text"
                id="subdomain"
                value={formData.subdomain}
                onChange={handleChange('subdomain')}
                placeholder="tfwc"
                className={errors.subdomain ? 'error' : ''}
                style={{ textTransform: 'lowercase' }}
              />
              <span className="subdomain-suffix">.clnicl.ie</span>
            </div>
            {subdomainStatus && (
              <span className={`subdomain-status ${subdomainStatus.className}`}>
                {subdomainStatus.text}
              </span>
            )}
            {errors.subdomain && (
              <span className="error-message">{errors.subdomain}</span>
            )}
            <p className="form-hint">
              Choose a short, memorable subdomain. Only lowercase letters, numbers, and hyphens.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="productType">
              Product <span className="required">*</span>
            </label>
            <select
              id="productType"
              value={formData.productType}
              onChange={handleChange('productType')}
              className={errors.productType ? 'error' : ''}
            >
              <option value="">Select a product</option>
              <option value="ppt">PCRS Power Tool (PPT)</option>
              <option value="pms">Clnicl PMS (full feature set)</option>
            </select>
            {errors.productType && (
              <span className="error-message">{errors.productType}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="contactPersonName">
              Contact Person Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="contactPersonName"
              value={formData.contactPersonName}
              onChange={handleChange('contactPersonName')}
              placeholder="John Doe"
              className={errors.contactPersonName ? 'error' : ''}
            />
            {errors.contactPersonName && (
              <span className="error-message">{errors.contactPersonName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="username">
              Username (Optional)
            </label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={handleChange('username')}
              placeholder="Choose a username for the initial user (optional)"
              className={errors.username ? 'error' : ''}
            />
            {errors.username && (
              <span className="error-message">{errors.username}</span>
            )}
            <p className="form-hint">
              Optional username for the initial user account. Admin role assignment will be a future feature.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number (optional)</label>
            <input
              type="tel"
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              placeholder="+353 1 234 5678"
            />
          </div>

          <div className="form-group legal-acceptance-group">
            <div className="checkbox-group legal-checkbox-row">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleCheckboxChange('termsAccepted')}
                className={errors.termsAccepted ? 'error' : ''}
              />
              <label htmlFor="termsAccepted">
                <span className="legal-consent-text">
                  I accept the{' '}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>{' '}
                  <span className="required">*</span>
                </span>
              </label>
            </div>
            {errors.termsAccepted && <span className="error-message">{errors.termsAccepted}</span>}
          </div>

          <div className="form-group legal-acceptance-group">
            <div className="checkbox-group legal-checkbox-row">
              <input
                type="checkbox"
                id="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleCheckboxChange('privacyAccepted')}
                className={errors.privacyAccepted ? 'error' : ''}
              />
              <label htmlFor="privacyAccepted">
                <span className="legal-consent-text">
                  I accept the{' '}
                  <a href="/privacy-notice" target="_blank" rel="noopener noreferrer">
                    Privacy Notice
                  </a>{' '}
                  <span className="required">*</span>
                </span>
              </label>
            </div>
            {errors.privacyAccepted && <span className="error-message">{errors.privacyAccepted}</span>}
          </div>

          <div className="form-group legal-acceptance-group">
            <div className="checkbox-group legal-checkbox-row">
              <input
                type="checkbox"
                id="pcrsResponsibilityAccepted"
                checked={formData.pcrsResponsibilityAccepted}
                onChange={handleCheckboxChange('pcrsResponsibilityAccepted')}
                className={errors.pcrsResponsibilityAccepted ? 'error' : ''}
              />
              <label htmlFor="pcrsResponsibilityAccepted">
                <span className="legal-consent-text">
                  I confirm that my practice/clinic is fully responsible and liable for all PCRS-related
                  activity and agrees to indemnify Clnicl as set out in the{' '}
                  <a href="/pcrs-responsibility" target="_blank" rel="noopener noreferrer">
                    PCRS Responsibility Statement
                  </a>{' '}
                  and Terms <span className="required">*</span>
                </span>
              </label>
            </div>
            {errors.pcrsResponsibilityAccepted && (
              <span className="error-message">{errors.pcrsResponsibilityAccepted}</span>
            )}
          </div>

          {submitError && (
            <div className="error-banner">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary btn-submit"
            disabled={isSubmitting || !subdomainAvailability.available}
          >
            {isSubmitting ? 'Registering...' : 'Register Clinic'}
          </button>

          <p className="register-footer">
            Already have an account?{' '}
            <a href="/login">Sign in to your clinic instance</a>
          </p>
          <p className="register-footer register-legal-links">
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</a> |{' '}
            <a href="/privacy-notice" target="_blank" rel="noopener noreferrer">Privacy</a> |{' '}
            <a href="/pcrs-responsibility" target="_blank" rel="noopener noreferrer">PCRS Statement</a>
          </p>
        </form>
      </div>
    </div>
  )
}








